import React, { useState, useRef } from 'react';
import { X, MapPin, Check, AlertCircle, Image as ImageIcon, Trash2 } from 'lucide-react';
import { submitStartup } from '../services/api';

// Resize a File client-side to a max 256px square, return a base64 data URL.
const readAndResize = (file, maxSize = 256) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        try {
          resolve(canvas.toDataURL('image/png'));
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const SubmitModal = ({ open, onClose, onSubmitted }) => {
  const [form, setForm] = useState({
    name: '', website: '', sector: '', description: '', address: '', email: '',
  });
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [logoError, setLogoError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  if (!open) return null;

  const handleChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const reset = () => {
    setForm({ name: '', website: '', sector: '', description: '', address: '', email: '' });
    setLogoDataUrl(null); setLogoError(null);
    setSubmitted(false); setError(null); setBusy(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoError(null);
    if (!file.type.startsWith('image/')) {
      setLogoError('Please pick an image file.');
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setLogoError('Image is too large (max 3 MB).');
      return;
    }
    try {
      const dataUrl = await readAndResize(file, 256);
      setLogoDataUrl(dataUrl);
    } catch (err) {
      setLogoError('Could not read that image, try another one.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      await submitStartup({ ...form, logo_data_url: logoDataUrl || undefined });
      setSubmitted(true);
      if (onSubmitted) onSubmitted();
      setTimeout(() => handleClose(), 1700);
    } catch (err) {
      try {
        const list = JSON.parse(localStorage.getItem('submittedStartups') || '[]');
        list.push({ ...form, submittedAt: new Date().toISOString() });
        localStorage.setItem('submittedStartups', JSON.stringify(list));
      } catch (_) { /* ignore */ }
      const msg = err?.response?.data?.detail || 'Could not reach server. Saved locally.';
      setError(typeof msg === 'string' ? msg : 'Submission failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-emerald-600" />
            <h3 className="font-semibold text-slate-900 text-[15px]">Submit your startup</h3>
          </div>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-100"
          >
            <X size={16} />
          </button>
        </div>

        {submitted ? (
          <div className="px-5 py-10 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 mx-auto flex items-center justify-center">
              <Check size={22} strokeWidth={3} />
            </div>
            <p className="mt-3 text-slate-900 font-medium">Thanks! We&apos;ll review your submission.</p>
            <p className="text-[13px] text-slate-500">We&apos;ll be in touch soon.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-3 overflow-y-auto">
            {/* Logo upload */}
            <div>
              <span className="block text-[12px] font-medium text-slate-700 mb-1">Logo <span className="text-slate-400 font-normal">(optional)</span></span>
              <div className="flex items-center gap-3">
                <div
                  onClick={() => fileRef.current && fileRef.current.click()}
                  className="w-16 h-16 rounded-xl border-2 border-dashed border-slate-200 hover:border-slate-400 bg-slate-50 flex items-center justify-center flex-shrink-0 cursor-pointer overflow-hidden"
                >
                  {logoDataUrl ? (
                    <img src={logoDataUrl} alt="logo preview" className="w-full h-full object-contain p-1" />
                  ) : (
                    <ImageIcon size={22} className="text-slate-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => fileRef.current && fileRef.current.click()}
                      className="text-[12.5px] font-medium bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded-md"
                    >
                      {logoDataUrl ? 'Change' : 'Upload'}
                    </button>
                    {logoDataUrl && (
                      <button
                        type="button"
                        onClick={() => setLogoDataUrl(null)}
                        className="inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-rose-600"
                      >
                        <Trash2 size={12} /> Remove
                      </button>
                    )}
                  </div>
                  <p className="text-[11.5px] text-slate-500 mt-1">PNG or JPG, resized to 256px on your device.</p>
                  {logoError && <p className="text-[11.5px] text-rose-600 mt-0.5">{logoError}</p>}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoPick}
                />
              </div>
            </div>

            <Field label="Startup name" required value={form.name} onChange={handleChange('name')} data-testid="submit-name-input" />
            <Field label="Website" placeholder="yourcompany.com" value={form.website} onChange={handleChange('website')} data-testid="submit-website-input" />
            <Field label="Sector" placeholder="e.g. Fintech, AI" value={form.sector} onChange={handleChange('sector')} data-testid="submit-sector-input" />
            <Field label="Address (city, OR)" required value={form.address} onChange={handleChange('address')} data-testid="submit-address-input" />
            <Field label="Short description" textarea value={form.description} onChange={handleChange('description')} data-testid="submit-description-input" />
            <Field label="Your email" type="email" required value={form.email} onChange={handleChange('email')} data-testid="submit-email-input" />

            {error && (
              <div className="flex items-start gap-2 text-[12.5px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-2">
                <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="submit-startup-form-button"
              className="w-full mt-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-medium text-[14px] py-2.5 rounded-lg transition-colors"
            >
              {busy ? 'Submitting…' : 'Submit startup'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, textarea, ...props }) => (
  <label className="block">
    <span className="block text-[12px] font-medium text-slate-700 mb-1">{label}</span>
    {textarea ? (
      <textarea
        {...props}
        rows={3}
        className="w-full text-[13px] px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 resize-none"
      />
    ) : (
      <input
        {...props}
        className="w-full text-[13px] px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
      />
    )}
  </label>
);

export default SubmitModal;
